import { Box, Button, Paper } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import CustomToolbar from "../components/CustomToolbar"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import { useQsFilters } from "../hooks/filters"
import { Persona } from "../types"
import { People } from "@mui/icons-material"
import { useSnackbarMessage } from "../hooks/snackbar"
import DialogConfirm from "../components/DialogConfirm"
import { usePersoneList, usePersonaCreate, usePersonaDelete, usePersonaUpdate } from "../hooks/persone"
import PersonaForm from "../components/forms/PersonaForm"
import dayjs from "dayjs"
import useUserCanWriteAndDelete from "../hooks/permissions"
import IconButtonDelete from "../components/IconButtonDelete"
import IconButtonEdit from "../components/IconButtonEdit"
import LangLink from "../components/LangLink"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "nome",
})

export default function Persone() {
  const pageSize = usePageSize()
  const { filters, setFilters, setFiltersDebounced, uiFilters } = useQsFilters(initFilters)
  const { t } = useTranslation()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "nome",
        headerName: t("persona.nome"),
        flex: 1,
        renderCell: (params) => (
          <LangLink style={{ color: "black" }} to={`/persone/${params.row.id}`}>
            {params.value}
          </LangLink>
        ),
      },
      {
        field: "data_nascita",
        headerName: t("persona.data_nascita"),
        flex: 1,
        renderCell: (params) => (params.value ? dayjs(params.value).format("DD/MM/YYYY") : ""),
      },
      {
        field: "data_morte",
        headerName: t("persona.data_morte"),
        flex: 1,
        renderCell: (params) => (params.value ? dayjs(params.value).format("DD/MM/YYYY") : ""),
      },
      {
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButtonEdit onClick={() => editModalActions.open(params.row)} />
            {userCanWriteAndDelete && <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} />}
          </>
        ),
      },
    ]
  }, [t])

  const { data, isLoading } = usePersoneList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
  })

  const { mutateAsync: create } = usePersonaCreate()
  const { mutateAsync: update } = usePersonaUpdate()
  const { mutateAsync: remove } = usePersonaDelete()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [editModalState, editModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0

    return count
  }, [])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("persona.titolo_lista")}
            breadcrumbs={[
              {
                label: t("persona.persone"),
                iconType: People,
              },
            ]}
          />
          {userCanWriteAndDelete && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("persona.cta_aggiungi")}
            </Button>
          )}
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1 })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            />
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Persona>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("persona.cta_aggiungi")}
        FormComponent={PersonaForm}
        save={(values: Persona) => {
          return create(values).then(() => {
            createModalActions.close()
          })
        }}
      />
      <DialogForm<Persona>
        open={editModalState.isOpen}
        handleClose={editModalActions.close}
        title={t("persona.cta_modifica")}
        FormComponent={PersonaForm}
        save={(values: Persona) => {
          return update(values).then(() => {
            editModalActions.close()
            showMessage(t("persona.esito_modifica_ok"))
          })
        }}
        item={editModalState.value}
        hasCancel
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("persona.cta_elimina")}
        subtitle={
          <span>
            {t("persona.cta_subtitle_elimina")} <b>{deleteModalState.value?.nome}</b> ?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("persona.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
